<template>
  <Container
    class="mx-auto mt-10 mb-30 min-h-[80%] w-full gap-5 md:w-[80.2%] lg:w-[72.3%] xl:mt-17.5 xl:mb-42.5 xl:gap-10"
  >
    <Transition name="fade" mode="out-in">
      <div
        v-if="isSubmitted"
        id="success-message"
        key="success"
        class="border-gray flex min-h-75 flex-col items-center justify-center gap-5 border p-6 text-center lg:min-h-100 lg:p-10"
      >
        <p class="lg:text-2.5xl/[110%] text-[22px]/[110%] font-medium">
          {{ t('alumni.leave.success_message') }}
        </p>
      </div>

      <div v-else key="form" class="flex flex-col gap-5 xl:gap-10">
        <h2 class="text-2.5xl xl:text-6xl/[100%]">
          {{ t('alumni.leave.title') }}
        </h2>

        <form
          @submit.prevent="handleSubmit"
          class="flex flex-col gap-4 lg:gap-7.5"
        >
          <FormInput
            id="email"
            type="email"
            :label="t('alumni.leave.email_label')"
            v-model="email"
            :error="error"
          />

          <button
            type="submit"
            class="cursor-pointer bg-black p-2.5 text-white lg:text-xl/[120%]"
            :aria-label="t('alumni.leave.submit_button')"
          >
            {{ t('alumni.leave.submit_button') }}
          </button>
        </form>
      </div>
    </Transition>
  </Container>
</template>

<script setup lang="ts">
import { z } from 'zod'

const { t, locale } = useI18n()

const { mutate } = useGqlMutation()

const isSubmitted = ref(false)
const email = ref('')
const error = ref('')

let timer: ReturnType<typeof setTimeout> | null = null

const dropoutSchema = computed(() =>
  z.object({
    email: z
      .string()
      .min(1, t('alumni.validation.email_required'))
      .email(t('alumni.validation.email_invalid')),
  }),
)

const resetForm = () => {
  email.value = ''
  error.value = ''
  isSubmitted.value = false
}

onUnmounted(() => {
  if (timer) clearTimeout(timer)
})

const handleSubmit = async () => {
  error.value = ''

  const result = dropoutSchema.value.safeParse({ email: email.value })

  if (!result.success) {
    error.value = result.error?.issues[0]?.message || ''
    return
  }

  try {
    await mutate('RequestLeaveAlumni', { email: result.data.email })
  } catch (err) {
    error.value = t('alumni.leave.user_not_found')
    return
  }

  isSubmitted.value = true

  nextTick(() => {
    const successEl = document.getElementById('success-message')
    if (successEl) {
      successEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  })

  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    resetForm()
  }, 7000)
}
</script>
